import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import {
  Button,
  LoadingSpinner,
  PageHeader,
  ErrorState,
} from '../components';
import { stripeService, eventService } from '../services';
import { useAuth } from '../context';

// Initialize Stripe
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder'
);

// Card element styling
const cardElementOptions = {
  style: {
    base: {
      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
      fontSize: '14px',
      color: '#000000',
      '::placeholder': {
        color: '#9ca3af',
      },
    },
    invalid: {
      color: '#dc2626',
      iconColor: '#dc2626',
    },
  },
};

/**
 * Inner form component with Stripe hooks
 */
function UpdatePaymentForm({ recoveryData, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [cardError, setCardError] = useState(null);

  // Handle card element change
  const handleCardChange = (e) => {
    setCardError(e.error ? e.error.message : null);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { clientSecret } = recoveryData;

      if (!clientSecret) {
        throw new Error('Invalid recovery session');
      }

      // Confirm card setup with Stripe
      const cardElement = elements.getElement(CardElement);
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      // Payment method updated successfully
      onSuccess(setupIntent.payment_method);
    } catch (err) {
      console.error('Payment update error:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Error message */}
      {error && (
        <div className="p-4 border-2 border-red-600 bg-red-50">
          <p className="font-mono text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Card Element */}
      <div>
        <label className="block font-mono text-sm font-semibold uppercase tracking-wider mb-2 text-black">
          New Payment Method <span className="text-red-600">*</span>
        </label>
        <div
          className={`
            px-4 py-4
            border-2 border-black
            bg-white
            ${cardError ? 'border-red-600' : ''}
          `}
        >
          <CardElement options={cardElementOptions} onChange={handleCardChange} />
        </div>
        {cardError && (
          <p className="mt-2 font-mono text-xs text-red-600 uppercase tracking-wider">
            {cardError}
          </p>
        )}
      </div>

      {/* Submit Button */}
      <div className="pt-4">
        <Button
          type="submit"
          variant="primary"
          fullWidth
          size="lg"
          disabled={isSubmitting || !stripe}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <LoadingSpinner size="sm" />
              UPDATING...
            </span>
          ) : (
            'UPDATE PAYMENT METHOD'
          )}
        </Button>
      </div>

      {/* Cancel link */}
      <div className="text-center">
        <button
          type="button"
          onClick={() => navigate('/guest')}
          className="font-mono text-sm text-gray-600 uppercase tracking-wider hover:text-black transition-colors"
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * PaymentRecoveryPage - Update Failed Payment Method
 * Design: Clear error messaging with Stripe form
 */
export default function PaymentRecoveryPage() {
  const navigate = useNavigate();
  const { applicationId } = useParams();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();

  const [recoveryData, setRecoveryData] = useState(null);
  const [event, setEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // Get application ID from URL params or query string
  const appId = applicationId || searchParams.get('applicationId');

  // Fetch recovery data
  useEffect(() => {
    const fetchRecoveryData = async () => {
      if (!appId) {
        setError('No application ID provided');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const result = await stripeService.getPaymentRecovery(appId);

        if (!result.success) {
          throw new Error(result.error || 'Failed to load payment recovery data');
        }

        setRecoveryData(result.data);

        // Fetch event details if available
        if (result.data.eventId) {
          const eventResult = await eventService.getEvent(result.data.eventId);
          if (eventResult.success) {
            setEvent(eventResult.data);
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchRecoveryData();
    }
  }, [appId, isAuthenticated]);

  // Handle successful payment update
  const handleSuccess = (paymentMethodId) => {
    setUpdateSuccess(true);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Auth check
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <h1 className="font-mono text-xl font-bold uppercase tracking-wider text-black mb-4">
            SIGN IN REQUIRED
          </h1>
          <p className="font-mono text-sm text-gray-600 mb-6">
            Please sign in to update your payment method.
          </p>
          <Button
            onClick={() => navigate(`/login?redirect=/payment-recovery/${appId}`)}
            fullWidth
          >
            SIGN IN
          </Button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <LoadingSpinner size="lg" label="Loading..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <ErrorState message={error} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  // Success state
  if (updateSuccess) {
    return (
      <div className="min-h-screen bg-white">
        <PageHeader
          title="Payment Updated!"
          breadcrumbs={[
            { label: 'Dashboard', onClick: () => navigate('/guest') },
            { label: 'Payment Recovery' },
          ]}
        />

        <div className="max-w-2xl mx-auto px-6 py-12 text-center">
          {/* Success icon */}
          <div className="w-20 h-20 mx-auto mb-6 border-4 border-green-500 flex items-center justify-center bg-green-50">
            <svg
              className="w-10 h-10 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="square"
                strokeLinejoin="miter"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <h2 className="font-mono text-2xl font-bold uppercase tracking-wider text-black mb-4">
            PAYMENT METHOD UPDATED
          </h2>

          <p className="font-mono text-sm text-gray-600 max-w-md mx-auto mb-8">
            Your payment method has been successfully updated. The payment will be retried automatically.
          </p>

          {event && (
            <div className="border-2 border-black p-6 text-left mb-8">
              <h3 className="font-mono text-lg font-bold uppercase tracking-wider text-black mb-4">
                {event.title}
              </h3>
              <div className="space-y-2">
                <p className="font-mono text-sm text-gray-600">
                  <span className="font-semibold">DATE:</span> {formatDate(event.date)}
                </p>
                <p className="font-mono text-sm text-gray-600">
                  <span className="font-semibold">LOCATION:</span> {event.location}
                </p>
              </div>
            </div>
          )}

          <Button onClick={() => navigate('/guest')} fullWidth>
            GO TO DASHBOARD
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <PageHeader
        title="Update Payment"
        breadcrumbs={[
          { label: 'Dashboard', onClick: () => navigate('/guest') },
          { label: 'Payment Recovery' },
        ]}
      />

      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Error banner */}
        <div className="mb-8 p-6 border-2 border-red-600 bg-red-50">
          <div className="flex items-start gap-4">
            {/* Error icon */}
            <div className="w-12 h-12 flex-shrink-0 border-2 border-red-600 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="square"
                  strokeLinejoin="miter"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div>
              <h2 className="font-mono text-lg font-bold uppercase tracking-wider text-red-800 mb-2">
                PAYMENT FAILED
              </h2>
              <p className="font-mono text-sm text-red-700">
                {recoveryData?.failureReason ||
                  'Your payment could not be processed. Please update your payment method to secure your spot.'}
              </p>
            </div>
          </div>
        </div>

        {/* Event info */}
        {event && (
          <div className="border-2 border-black p-6 mb-8">
            <h3 className="font-mono text-lg font-bold uppercase tracking-wider text-black mb-4">
              {event.title}
            </h3>
            <div className="space-y-2">
              <p className="font-mono text-sm text-gray-600">
                <span className="font-semibold uppercase">DATE:</span>{' '}
                {formatDate(event.date)}
              </p>
              <p className="font-mono text-sm text-gray-600">
                <span className="font-semibold uppercase">LOCATION:</span>{' '}
                {event.location}
              </p>
              <p className="font-mono text-sm text-gray-600">
                <span className="font-semibold uppercase">AMOUNT DUE:</span> $
                {((recoveryData?.amountCents || 0) / 100).toFixed(2)}
              </p>
            </div>
          </div>
        )}

        {/* Amount due (if no event info) */}
        {!event && recoveryData?.amountCents && (
          <div className="border-2 border-black p-6 mb-8">
            <div className="text-center">
              <p className="font-mono text-sm text-gray-600 uppercase tracking-wider mb-2">
                AMOUNT DUE
              </p>
              <p className="font-mono text-3xl font-bold text-black">
                ${(recoveryData.amountCents / 100).toFixed(2)}
              </p>
            </div>
          </div>
        )}

        {/* Update payment form */}
        <div className="border-2 border-black p-6">
          <h3 className="font-mono text-sm font-semibold uppercase tracking-wider text-gray-600 mb-6">
            ENTER NEW PAYMENT METHOD
          </h3>

          <Elements stripe={stripePromise}>
            <UpdatePaymentForm
              recoveryData={recoveryData}
              onSuccess={handleSuccess}
            />
          </Elements>
        </div>

        {/* Help text */}
        <div className="mt-6 p-4 bg-gray-50 border-2 border-gray-200">
          <p className="font-mono text-xs text-gray-600">
            <span className="font-semibold">NEED HELP?</span> If you continue to
            experience payment issues, please contact support or try a different
            payment method.
          </p>
        </div>
      </div>
    </div>
  );
}
